import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { PipesModule } from '@app/core/pipes/pipes.module';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { AccountService, BillingV2Service, ConfigService, FilesService } from '@core/services';
import { DestroyableComponent } from '@models/destroyable.component';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';

import { ImageSizeEnum, Subject, User, UserProfile } from 'lingo2-models';
import { OnUiCover, OnUiCoverModule } from 'onclass-ui';
import { Observable, Subject as RxJsSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-new-teacher-card-mini',
  templateUrl: './new-teacher-card-mini.component.html',
  styleUrls: ['./new-teacher-card-mini.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [LocaleSharedModule, CommonModule, OnUiCoverModule, PipesModule],
})
export class NewTeacherCardMiniComponent extends DestroyableComponent implements OnInit, OnDestroy {
  errorNotificationService = inject(ErrorNotificationService);
  private billingV2Service = inject(BillingV2Service);
  private configService = inject(ConfigService);

  @Input() public set user(user: User) {
    this._user = user;
    this._profile = user.profile;
    this.init();
  }

  public countryName: string;
  public countryFlagUrl: string;
  public url: string;
  public name: string;
  public teachingSubjects: Subject[] = [];
  public mainTeachingSubject: Subject;
  public othersSubjectLength: number;
  public cover: OnUiCover;

  private _user: User;
  private _profile: Partial<UserProfile>;
  private _subjects: Subject[];
  private ngUnsubscribe = new RxJsSubject();

  ngOnInit() {
    this.configService.subjects$.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (subjects) => {
        this._subjects = subjects;
        this.init();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next(true);
    this.ngUnsubscribe.complete();
  }

  protected init() {
    if (!this._user || !this._profile || !this._subjects) {
      return;
    }

    this.url = AccountService.accountRoute(this._user).join('/');
    this.name = AccountService.getUserFullName(this._user);
    this.cover = {
      type: 'avatar',
      img: FilesService.getFileUrlBySize(this._user?.userpic_id, ImageSizeEnum.md),
      title: this.name,
      form: 'circle',
    };

    if (this._user.country) {
      this.countryName = this._user.country.title;
      if (this._user.country.flag_url) {
        this.countryFlagUrl = this._user.country.flag_url;
      }
    }

    this.teachingSubjects = (this._profile.teaching_subjects || [])
      .filter((ts) => ts.subject_id > 0)
      .map((subject) => {
        const _subject = this._subjects.find((s) => s.id === +subject.subject_id);
        return _subject || null;
      });
    this.mainTeachingSubject = this.teachingSubjects.length ? this.teachingSubjects[0] : null;
    this.othersSubjectLength = this.teachingSubjects.slice(1).length;
  }

  public getFormattedPrice(amount: number, discount?: number): Observable<string> {
    return this.billingV2Service.formatAmount$(amount, discount);
  }

  public get minPriceTier(): number {
    return this._user ? this._user.stats.min_price_tier : null;
  }
}
